import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

const feedbackActionButtonSx = {
  minWidth: 0,
  px: 0.25,
  py: 0.35,
  color: "rgba(246,192,78,0.96)",
  fontSize: 12,
  textTransform: "none",
  "& .MuiButton-startIcon": {
    mr: 0.75,
    color: "inherit",
  },
  "&:hover": {
    bgcolor: "rgba(219,165,41,0.1)",
  },
};

const feedbackCancelButtonSx = {
  ...feedbackActionButtonSx,
  color: "rgba(234,231,226,0.72)",
  "&:hover": {
    bgcolor: "rgba(255,255,255,0.08)",
  },
};

export default function FeedbackModal({
  open,
  rating,
  comment,
  loading = false,
  onClose,
  onCommentChange,
  onSubmit,
}) {
  const title =
    rating === "Good"
      ? "What did you like about this response?"
      : "How can we improve this response?";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: 430,
          maxWidth: "92vw",
          borderRadius: "24px",
          color: "white",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
          border: "1px solid rgba(219,165,41,0.26)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.12), 0 14px 34px rgba(0,0,0,0.18)",
          backdropFilter: "blur(18px) saturate(120%)",
          WebkitBackdropFilter: "blur(18px) saturate(120%)",
          maxHeight: "calc(100vh - 64px)",
          "@media (max-width: 767px)": {
            maxHeight: "calc(100dvh - 64px)",
          },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 800 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1.25 }}>
          <TextField
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            maxRows={5}
            placeholder="Share your feedback..."
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "rgba(250,246,239,0.94)",
                fontSize: 14,
                borderRadius: "16px",
                bgcolor: "rgba(255,255,255,0.055)",
                "& fieldset": {
                  borderColor: "rgba(255,255,255,0.16)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(219,165,41,0.42)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(219,165,41,0.7)",
                },
              },
              "& .MuiInputBase-inputMultiline": {
                overflowY: "auto !important",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                pr: 0.75,
                "&::-webkit-scrollbar": {
                  width: 5,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.24)",
                  borderRadius: "999px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(234,231,226,0.46)",
                opacity: 1,
              },
            }}
          />
        </Box>
      </DialogContent>
      <Divider sx={{ mx: 3, borderColor: "rgba(255,255,255,0.14)" }} />
      <DialogActions
        sx={{
          px: 3,
          py: 1.4,
          justifyContent: "flex-start",
          gap: 1.5,
        }}
      >
        <Button
          size="small"
          startIcon={<SendRoundedIcon fontSize="small" />}
          onClick={onSubmit}
          disabled={loading}
          sx={feedbackActionButtonSx}
        >
          Submit
        </Button>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: "rgba(255,255,255,0.18)" }}
        />
        <Button
          size="small"
          startIcon={<CloseRoundedIcon fontSize="small" />}
          onClick={onClose}
          disabled={loading}
          sx={feedbackCancelButtonSx}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
